import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  Typography,
  MenuItem,
  Select,
  FormControl,
  Alert,
} from "@mui/material";
import { styled } from "@mui/material/styles";
// import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteModal from "../common/DeleteModal";
import config from "../../config";
import axios from "axios";
import {
  DownloadIcon,
  ReplaceIcon,
  TrashIcon,
  UploadCloudIcon,
  ImageUploadIcon,
} from "./icons";
import { useNavigate } from "react-router";
import { buildAssignDriverVehiclePayload } from "../../utils/driverPayloads";
import { useTranslation } from "react-i18next";

const FormContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#E8EAEE",
  borderRadius: "10px",
  borderBottom: "1px solid #C8CBD0",
  padding: "24px",
  marginBottom: "20px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  position: "relative",
}));

const StyledTextField = styled(TextField)(({ theme, error }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: "16px",
    height: "55px",
    "& fieldset": {
      borderColor: error ? "#d32f2f" : "#183059",
      borderWidth: "2px",
    },
    "&:hover fieldset": {
      borderColor: error ? "#d32f2f" : "#183059",
    },
    "&.Mui-focused fieldset": {
      borderColor: error ? "#d32f2f" : "#183059",
    },
    "&.Mui-disabled": {
      backgroundColor: "#f5f5f5",
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: "16px 24px",
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  backgroundColor: "#FFFFFF",
  borderRadius: "16px",
  height: "55px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#183059",
    borderWidth: "2px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#183059",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#183059",
  },
  "& .MuiSelect-select": {
    padding: "16px 24px",
  },
}));

const ConfirmButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#F9E719",
  color: "#1F242E",
  height: "55px",
  border: "1px solid #414958",
  borderRadius: "8px",
  padding: "16px 24px",
  fontWeight: "bold",
  textTransform: "none",
  fontSize: "20px",
  "&:hover": {
    backgroundColor: "#FBC02D",
  },
}));

const CancelButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#F2F2F3",
  height: "55px",
  borderRadius: "8px",
  padding: "16px 24px",
  fontWeight: "bold",
  border: "1px solid #414958",
  color: "#1F242E",
  fontSize: "20px",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#F2F2F3",
  },
}));

const ImageUploadBox = styled(Box)(({ theme }) => ({
  border: "2px dashed #183059",
  width: "100%",
  height: "100%",
  borderRadius: "16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  overflow: "hidden",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "#e8e8e8",
  },
}));

const DeleteButton = styled(Button)(({ theme }) => ({
  color: "#cc0000",
  height: "55px",
  borderRadius: "8px",
  padding: "16px 24px",
  fontSize: "20px",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "rgba(204, 0, 0, 0.05)",
  },
}));

const REQUIRED_ASTERISK_SX = {
  color: "#d32f2f",
  ml: 0.5,
};

const CURRENT_YEAR = new Date().getFullYear();

export default function CreateVehicleForm({
  onClose,
  onSubmit,
  initialData = null,
  isEdit = false,
  onDelete,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [uploadedDocuments, setUploadedDocuments] = useState(
    initialData?.uploadedDocuments || [],
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [formData, setFormData] = useState(
    initialData || {
      vehicleName: "",
      model: "",
      registrationNumber: "",
      chassisNumber: "",
      make: "",
      yearOfManufacture: "",
      route: "",
      driver: "",
      attendant: "",
      status: "",
      profileImage: null,
      documents: [],
      device_id: "",
    },
  );

  const [imagePreview, setImagePreview] = useState(
    initialData?.profileImage || null,
  );
  const [routeData, setRouteData] = useState([]);
  const [driverData, setDriverData] = useState([]);
  const [attendantData, setAttendantData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [orgId, setOrgId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    try {
      const userStateString = localStorage.getItem("userState");
      if (userStateString) {
        const userState = JSON.parse(userStateString);
        const organizationId = userState.user?.organizationId;
        const currentUserId = userState.user?.id;

        if (organizationId) {
          setOrgId(organizationId);
          setUserId(currentUserId);
        }
      }
    } catch (err) {
      console.error("Error reading user state:", err);
      setError("Failed to load user information");
    }
  }, []);

  useEffect(() => {
    if (!orgId) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const [routesResponse, driversResponse, attendantsResponse] =
          await Promise.all([
            fetch(
              `${config.baseURL}/route/organization?organizationId=${orgId}`,
              { credentials: "include" },
            ),
            fetch(
              `${config.baseURL}/driver/organization?organizationId=${orgId}`,
              { credentials: "include" },
            ),
            fetch(
              `${config.baseURL}/employee/organization?organizationId=${orgId}`,
              { credentials: "include" },
            ),
          ]);

        if (routesResponse.ok) {
          const routesData = await routesResponse.json();
          setRouteData(Array.isArray(routesData) ? routesData : []);
        }

        if (driversResponse.ok) {
          const driversData = await driversResponse.json();
          setDriverData(Array.isArray(driversData) ? driversData : []);
        }

        if (attendantsResponse.ok) {
          const attendantsData = await attendantsResponse.json();
          setAttendantData(Array.isArray(attendantsData) ? attendantsData : []);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load form data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [orgId]);

  const availableDocTypes = [
    "RC Book",
    "Emission Test Report",
    "Insurance Policy",
  ].filter((type) => !uploadedDocuments.some((doc) => doc.type === type));
  const getDocumentTypeLabel = (type) => t(type);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData({ ...formData, [field]: value });

    // Per-field validation
    let error = "";
    if (field === "vehicleName") {
      if (!value.trim()) {
        error = t("Vehicle Name is required");
      } else if (!/^[a-zA-Z0-9\s]+$/.test(value.trim())) {
        error = t("Vehicle Name can contain only letters, numbers, and spaces");
      }
    }
    if (field === "registrationNumber") {
      if (!value.trim()) {
        error = t("Registration Number is required");
      } else if (!/^[a-zA-Z0-9-\s]+$/.test(value.trim())) {
        error = t(
          "Registration Number can contain only letters, numbers, spaces, and hyphen",
        );
      }
    }
    if (field === "make") {
      if (value.trim() && !/^[a-zA-Z0-9\s&.,'()\-_/]+$/.test(value.trim())) {
        error = t(
          "Make/Brand can include letters, numbers, spaces, and special characters (& . , ' ( ) - _ /)",
        );
      }
    }
    if (field === "model") {
      if (value.trim() && !/^[a-zA-Z0-9\s&.,'()\-_/]+$/.test(value.trim())) {
        error = t(
          "Model can include letters, numbers, spaces, and special characters (& . , ' ( ) - _ /)",
        );
      }
    }
    if (field === "chassisNumber") {
      if (value.trim() && !/^[a-zA-Z0-9-]+$/.test(value.trim())) {
        error = t("Chassis Number can contain only letters, numbers, and hyphen");
      }
    }
    if (field === "yearOfManufacture") {
      if (value.trim()) {
        const year = Number(value);
        if (!/^\d{4}$/.test(value.trim())) {
          error = t("Year of Manufacture must be 4 digits");
        } else if (year < 1990 || year > CURRENT_YEAR) {
          error = t("Year of Manufacture must be between 1990 and {{year}}", {
            year: CURRENT_YEAR,
          });
        }
      }
    }
    setFieldErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        profileImage: file,
      });
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDocumentUpload = (event, docType) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedDocuments([
          ...uploadedDocuments,
          {
            type: docType,
            image: reader.result,
            file: file,
            isExisting: false,
            isUploaded: false,
          },
        ]);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDocTypeChange = (index, newType) => {
    const updatedDocs = [...uploadedDocuments];
    updatedDocs[index].type = newType;
    setUploadedDocuments(updatedDocs);
  };

  const handleDocumentDelete = async (index) => {
    const docToRemove = uploadedDocuments[index];

    // If this is an existing uploaded document, call the API to delete it
    if (docToRemove.isExisting && docToRemove.isUploaded && initialData?.id) {
      try {
        setLoading(true);
        await axios.delete(`${config.baseURL}/upload/image`, {
          params: {
            name: docToRemove.type || "Other",
            associateEntityId: initialData.id,
            associatedWith: "Vehicle",
          },
          withCredentials: true,
        });

        console.log(`Successfully deleted document: ${docToRemove.type}`);

        setStatusMessage({
          type: "success",
          message: "Document deleted successfully",
        });
      } catch (error) {
        console.error("Error deleting document:", error);
        setStatusMessage({
          type: "error",
          message: `Failed to delete document: ${
            error.response?.data?.error || error.message
          }`,
        });
        setLoading(false);
        return;
      } finally {
        setLoading(false);
      }
    }

    // Remove from local state
    const updatedDocs = uploadedDocuments.filter((_, i) => i !== index);
    setUploadedDocuments(updatedDocs);
  };

  const handleDocumentReplace = (index, event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const updatedDocs = [...uploadedDocuments];
        updatedDocs[index].image = reader.result;
        updatedDocs[index].file = file;
        updatedDocs[index].needsReplacement = updatedDocs[index].isExisting;
        setUploadedDocuments(updatedDocs);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    setShowDeleteModal(false);
    if (onDelete) {
      onDelete();
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };

  const uploadVehicleImage = async (vehicleId) => {
    if (!formData.profileImage || !(formData.profileImage instanceof File)) {
      return null;
    }

    const imageFormData = new FormData();
    imageFormData.append("image", formData.profileImage);

    const response = await fetch(
      `${config.baseURL}/upload/image?name=VehicleImage&associateEntityId=${vehicleId}&associatedWith=Vehicle&type=profile`,
      {
        method: "POST",
        credentials: "include",
        body: imageFormData,
      },
    );

    if (!response.ok) {
      throw new Error("Image upload failed");
    }

    return await response.json();
  };

  const uploadDocuments = async (vehicleId) => {
    const uploadPromises = uploadedDocuments.map(async (doc) => {
      if (!doc.file) return null;

      // If document needs replacement, delete the old one first
      if (doc.isExisting && doc.needsReplacement) {
        try {
          await axios.delete(`${config.baseURL}/upload/image`, {
            params: {
              name: doc.type || "Other",
              associateEntityId: vehicleId,
              associatedWith: "Vehicle",
            },
            withCredentials: true,
          });
        } catch (deleteError) {
          console.warn("Failed to delete old document:", deleteError);
        }
      }

      const docFormData = new FormData();
      docFormData.append("image", doc.file);

      const response = await fetch(
        `${config.baseURL}/upload/image?name=${encodeURIComponent(
          doc.type,
        )}&associateEntityId=${vehicleId}&associatedWith=Vehicle&type=document`,
        {
          method: "POST",
          credentials: "include",
          body: docFormData,
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to upload ${doc.type}`);
      }

      return await response.json();
    });

    return Promise.all(uploadPromises);
  };

  const assignVehicleToRoute = async (vehicleId, routeId) => {
    if (!routeId) return;

    const response = await fetch(`${config.baseURL}/vehicle/route`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        routeId,
        vehicleId,
        performedByUserId: userId,
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to assign route");
    }
  };

  const assignVehicleToDriver = async (vehicleId, driverId) => {
    if (!driverId) return;

    const response = await fetch(`${config.baseURL}/driver/vehicle`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(
        buildAssignDriverVehiclePayload(driverId, vehicleId),
      ),
    });

    if (!response.ok) {
      throw new Error("Failed to assign driver");
    }
  };

  const assignVehicleToAttendant = async (vehicleId, attendantId) => {
    if (!attendantId) return;

    const response = await fetch(`${config.baseURL}/employee/vehicle`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        employeeId: attendantId,
        vehicleId,
        performedByUserId: userId,
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to assign attendant");
    }
  };

  const validateFields = () => {
    const newErrors = {};
    if (!formData.vehicleName?.trim()) {
      newErrors.vehicleName = t("Vehicle Name is required");
    } else if (!/^[a-zA-Z0-9\s]+$/.test(formData.vehicleName.trim())) {
      newErrors.vehicleName = t(
        "Vehicle Name can contain only letters, numbers, and spaces",
      );
    }
    if (!formData.registrationNumber?.trim()) {
      newErrors.registrationNumber = t("Registration Number is required");
    } else if (!/^[a-zA-Z0-9-\s]+$/.test(formData.registrationNumber.trim())) {
      newErrors.registrationNumber = t(
        "Registration Number can contain only letters, numbers, spaces, and hyphen",
      );
    }
    if (
      formData.make?.trim() &&
      !/^[a-zA-Z0-9\s]+$/.test(formData.make.trim())
    ) {
      newErrors.make = t("Make/Brand can contain only letters, numbers, and spaces");
    }
    if (
      formData.model?.trim() &&
      !/^[a-zA-Z0-9\s]+$/.test(formData.model.trim())
    ) {
      newErrors.model = t("Model can contain only letters, numbers, and spaces");
    }
    if (
      formData.chassisNumber?.trim() &&
      !/^[a-zA-Z0-9-]+$/.test(formData.chassisNumber.trim())
    ) {
      newErrors.chassisNumber = t(
        "Chassis Number can contain only letters, numbers, and hyphen",
      );
    }
    if (formData.yearOfManufacture?.trim()) {
      const year = Number(formData.yearOfManufacture);
      if (!/^\d{4}$/.test(formData.yearOfManufacture.trim())) {
        newErrors.yearOfManufacture = t("Year of Manufacture must be 4 digits");
      } else if (year < 1990 || year > CURRENT_YEAR) {
        newErrors.yearOfManufacture = t(
          "Year of Manufacture must be between 1990 and {{year}}",
          { year: CURRENT_YEAR },
        );
      }
    }
    setFieldErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateFields()) return;

    setLoading(true);
    setError(null);
    setStatusMessage(null);

    try {
      const vehiclePayload = {
        name: formData.vehicleName,
        VehicleNumber: formData.registrationNumber,
        Model: formData.model,
        chassisNumber: formData.chassisNumber,
        Make: formData.make,
        Year: formData.yearOfManufacture,
        organizationId: orgId,
        performedByUserId: userId,
        description: "-",
      };

      if (isEdit && formData.device_id) {
        vehiclePayload.device_id = formData.device_id;
      }

      let vehicleResponse;
      if (isEdit && initialData?.id) {
        vehicleResponse = await fetch(`${config.baseURL}/vehicle`, {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...vehiclePayload, id: initialData.id }),
        });
      } else {
        vehicleResponse = await fetch(`${config.baseURL}/vehicle`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(vehiclePayload),
        });
      }

      if (!vehicleResponse.ok) {
        throw new Error("Failed to save vehicle");
      }

      const vehicleData = await vehicleResponse.json();
      const vehicleId = isEdit ? initialData.id : vehicleData.vehicle.id;

      if (formData.profileImage) {
        await uploadVehicleImage(vehicleId);
      }

      if (uploadedDocuments.length > 0) {
        await uploadDocuments(vehicleId);
      }

      const assignments = [];
      if (formData.route) {
        assignments.push(assignVehicleToRoute(vehicleId, formData.route));
      }
      if (formData.driver) {
        assignments.push(assignVehicleToDriver(vehicleId, formData.driver));
      }
      if (formData.attendant) {
        assignments.push(
          assignVehicleToAttendant(vehicleId, formData.attendant),
        );
      }

      await Promise.all(assignments);

      setStatusMessage({
        type: "success",
        message: isEdit
          ? t("Vehicle updated successfully!")
          : t("Vehicle created successfully!"),
      });

      if (onSubmit) {
        onSubmit({ ...formData, id: vehicleId });
      }

      if (!isEdit) {
        setFormData({
          vehicleName: "",
          model: "",
          registrationNumber: "",
          chassisNumber: "",
          make: "",
          yearOfManufacture: "",
          route: "",
          driver: "",
          attendant: "",
          status: "",
          profileImage: null,
          documents: [],
        });
        setImagePreview(null);
        setUploadedDocuments([]);
      }
      navigate(".", { replace: true });
    } catch (err) {
      console.error("Error saving vehicle:", err);
      setError(err.message || "Failed to save vehicle");
      setStatusMessage({
        type: "error",
        message: "Failed to save vehicle. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };
  const handleProfileImageDelete = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Only call the API if it's an existing saved image (edit mode)
    if (isEdit && initialData?.id && !(formData.profileImage instanceof File)) {
      try {
        setLoading(true);
        await axios.delete(`${config.baseURL}/upload/image`, {
          params: {
            name: "VehicleImage",
            associateEntityId: initialData.id,
            associatedWith: "Vehicle",
          },
          withCredentials: true,
        });

        setStatusMessage({
          type: "success",
          message: t("Profile image deleted successfully"),
        });
      } catch (error) {
        console.error("Error deleting profile image:", error);
        setStatusMessage({
          type: "error",
          message: `${t("Failed to delete image")}: ${
            error.response?.data?.error || error.message
          }`,
        });
        setLoading(false);
        return;
      } finally {
        setLoading(false);
      }
    }

    // Clear local state regardless
    setImagePreview(null);
    setFormData((prev) => ({ ...prev, profileImage: null }));
  };
  const UploadedBox = ({ docType, image, index }) => {
    return (
      <Grid item xs={4}>
        <Box
          sx={{
            maxHeight: "460px",
            width: "auto",
            borderRadius: "16px",
            border: "1px solid #183059",
            padding: "24px",
          }}
        >
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <Select
              displayEmpty
              value={docType}
              onChange={(e) => handleDocTypeChange(index, e.target.value)}
              renderValue={(selected) =>
                selected ? getDocumentTypeLabel(selected) : t("Select Document Type")
              }
              sx={{
                backgroundColor: "#B7BFCC",
                boxShadow: "-2px 2px 5px 0px #00000011",
                height: "48px",
                borderRadius: "8px",
                width: "calc(100% - 60px)",
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: "20px",
                color: "#111214",
              }}
            >
              <MenuItem value="" disabled>
                {t("Select Document Type")}
              </MenuItem>
              <MenuItem value={docType}>{getDocumentTypeLabel(docType)}</MenuItem>
              {availableDocTypes
                .filter((type) => type !== docType)
                .map((type) => (
                  <MenuItem key={type} value={type}>
                    {getDocumentTypeLabel(type)}
                  </MenuItem>
                ))}
            </Select>

            <Button
              component="span"
              onClick={async () => {
                try {
                  let downloadUrl = image;
                  let fileName = docType || "document";
                  if (
                    image &&
                    !image.startsWith("data:") &&
                    !image.startsWith("blob:")
                  ) {
                    const resp = await axios.get(image, {
                      responseType: "blob",
                      withCredentials: true,
                    });
                    downloadUrl = URL.createObjectURL(resp.data);
                  }
                  const link = document.createElement("a");
                  link.href = downloadUrl;
                  link.download = fileName;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                } catch (e) {
                  console.error("Download failed:", e);
                }
              }}
              sx={{
                border: "1px solid #414958",
                padding: "10px 12px",
                height: "48px",
                width: "48px",
                borderRadius: "8px",
                minWidth: "48px",
              }}
            >
              <DownloadIcon />
            </Button>
          </Box>
          <Box
            sx={{
              padding: "10px",
              height: "268px",
              backgroundColor: "#b7bfcc4c",
              borderRadius: "8px",
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            <img
              src={image}
              alt={docType}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <input
              accept="image/*,.pdf"
              style={{ display: "none" }}
              id={`replace-doc-${index}`}
              type="file"
              onChange={(e) => handleDocumentReplace(index, e)}
            />
            <label htmlFor={`replace-doc-${index}`}>
              <Button
                component="span"
                sx={{
                  backgroundColor: "#20A8E0",
                  height: "48px",
                  borderRadius: "8px",
                  padding: "12px 24px",
                  display: "flex",
                  gap: "10px",
                  color: "#FFFFFF",
                  fontFamily: "Roboto",
                  fontWeight: 500,
                  fontSize: "20px",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#1890c0",
                  },
                }}
              >
                <ReplaceIcon />
                {t("Change")}
              </Button>
            </label>
            <Button
              onClick={() => handleDocumentDelete(index)}
              sx={{
                backgroundColor: "#EE3B2B",
                height: "48px",
                borderRadius: "8px",
                padding: "12px 24px",
                display: "flex",
                gap: "10px",
                color: "#FFFFFF",
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: "20px",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#d42a1a",
                },
              }}
            >
              <TrashIcon />
              {t("Delete")}
            </Button>
          </Box>
        </Box>
      </Grid>
    );
  };

  const UploadBox = () => {
    const [selectedType, setSelectedType] = useState("");

    return (
      <Grid item xs={4}>
        <Box
          sx={{
            maxHeight: "460px",
            width: "auto",
            borderRadius: "16px",
            border: "1px solid #183059",
            padding: "24px",
          }}
        >
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <Select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              renderValue={(selected) =>
                selected ? getDocumentTypeLabel(selected) : t("Select Document Type")
              }
              sx={{
                backgroundColor: "#B7BFCC",
                boxShadow: "-2px 2px 5px 0px #00000011",
                height: "48px",
                borderRadius: "8px",
                width: "100%",
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: "20px",
                color: "#111214",
              }}
              displayEmpty
            >
              <MenuItem value="" disabled>
                {t("Select Document Type")}
              </MenuItem>
              {availableDocTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {getDocumentTypeLabel(type)}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <input
            accept="image/*,.pdf"
            style={{ display: "none" }}
            id="new-document-upload"
            type="file"
            onChange={(e) => {
              if (selectedType) {
                handleDocumentUpload(e, selectedType);
                setSelectedType("");
              }
            }}
            disabled={!selectedType}
          />
          <label htmlFor="new-document-upload">
            <Box
              sx={{
                padding: "10px",
                height: "268px",
                backgroundColor: "#b7bfcc4c",
                borderRadius: "8px",
                mb: 2,
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                color: "#414958",
                fontFamily: "Roboto",
                fontWeight: 400,
                fontSize: 20,
                cursor: selectedType ? "pointer" : "not-allowed",
                opacity: selectedType ? 1 : 0.5,
              }}
            >
              {t("+ New Document")}
            </Box>
          </label>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <label htmlFor="new-document-upload">
              <Button
                component="span"
                disabled={!selectedType}
                sx={{
                  backgroundColor: "#20A8E0",
                  height: "48px",
                  borderRadius: "8px",
                  padding: "12px 24px",
                  display: "flex",
                  gap: "10px",
                  color: "#FFFFFF",
                  fontFamily: "Roboto",
                  fontWeight: 500,
                  fontSize: "20px",
                  textTransform: "none",
                  "&:disabled": {
                    backgroundColor: "#ccc",
                    color: "#666",
                  },
                  "&:hover": {
                    backgroundColor: selectedType ? "#1890c0" : "#ccc",
                  },
                }}
              >
                <UploadCloudIcon />
                {t("Upload")}
              </Button>
            </label>
          </Box>
        </Box>
      </Grid>
    );
  };

  return (
    <FormContainer>
      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={9}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontWeight: 400,
                  fontFamily: "Roboto",
                  fontSize: "20px",
                  color: "#414958",
                }}
              >
                {t("Name")}
                <Box component="span" sx={REQUIRED_ASTERISK_SX}>
                  *
                </Box>
              </Typography>
              <StyledTextField
                fullWidth
                value={formData.vehicleName}
                onChange={handleChange("vehicleName")}
                placeholder={t("Vehicle Name")}
                disabled={loading}
                error={!!fieldErrors.vehicleName}
                helperText={fieldErrors.vehicleName}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontWeight: 400,
                  fontFamily: "Roboto",
                  fontSize: "20px",
                  color: "#414958",
                }}
              >
                {t("Reg. No.")}
                <Box component="span" sx={REQUIRED_ASTERISK_SX}>
                  *
                </Box>
              </Typography>
              <StyledTextField
                fullWidth
                value={formData.registrationNumber}
                onChange={handleChange("registrationNumber")}
                placeholder={t("Registration Number")}
                disabled={loading}
                error={!!fieldErrors.registrationNumber}
                helperText={fieldErrors.registrationNumber}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontWeight: 400,
                  fontFamily: "Roboto",
                  fontSize: "20px",
                  color: "#414958",
                }}
              >
                {t("Make/Brand")}
              </Typography>
              <StyledTextField
                fullWidth
                value={formData.make}
                onChange={handleChange("make")}
                placeholder={t("Make/Brand")}
                disabled={loading}
                error={!!fieldErrors.make}
                helperText={fieldErrors.make}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontWeight: 400,
                  fontFamily: "Roboto",
                  fontSize: "20px",
                  color: "#414958",
                }}
              >
                {t("Model")}
              </Typography>
              <StyledTextField
                fullWidth
                value={formData.model}
                onChange={handleChange("model")}
                placeholder={t("Model")}
                disabled={loading}
                error={!!fieldErrors.model}
                helperText={fieldErrors.model}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontWeight: 400,
                  fontFamily: "Roboto",
                  fontSize: "20px",
                  color: "#414958",
                }}
              >
                {t("Chassis Number")}
              </Typography>
              <StyledTextField
                fullWidth
                value={formData.chassisNumber}
                onChange={handleChange("chassisNumber")}
                placeholder={t("Chassis Number")}
                disabled={loading}
                error={!!fieldErrors.chassisNumber}
                helperText={fieldErrors.chassisNumber}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontWeight: 400,
                  fontFamily: "Roboto",
                  fontSize: "20px",
                  color: "#414958",
                }}
              >
                {t("Year of Manufacture")}
              </Typography>
              <StyledTextField
                fullWidth
                type="text"
                value={formData.yearOfManufacture}
                onChange={handleChange("yearOfManufacture")}
                placeholder={t("Year")}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  min: 1990,
                  max: CURRENT_YEAR,
                }}
                disabled={loading}
                error={!!fieldErrors.yearOfManufacture}
                helperText={fieldErrors.yearOfManufacture}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontWeight: 400,
                  fontFamily: "Roboto",
                  fontSize: "20px",
                  color: "#414958",
                }}
              >
                {t("Route")}
              </Typography>
              <FormControl fullWidth>
                <StyledSelect
                  value={formData.route}
                  onChange={handleChange("route")}
                  displayEmpty
                  disabled={loading}
                >
                  <MenuItem value="" disabled>
                    {t("Select Route")}
                  </MenuItem>
                  {routeData.map((route) => (
                    <MenuItem key={route.id} value={route.id}>
                      {route.name}
                    </MenuItem>
                  ))}
                </StyledSelect>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontWeight: 400,
                  fontFamily: "Roboto",
                  fontSize: "20px",
                  color: "#414958",
                }}
              >
                {t("Driver")}
              </Typography>
              <FormControl fullWidth>
                <StyledSelect
                  value={formData.driver}
                  onChange={handleChange("driver")}
                  displayEmpty
                  disabled={loading}
                >
                  <MenuItem value="" disabled>
                    {t("Select Driver")}
                  </MenuItem>
                  {driverData.map((driver) => (
                    <MenuItem key={driver.id} value={driver.id}>
                      {driver.first_name} {driver.last_name}
                    </MenuItem>
                  ))}
                </StyledSelect>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontWeight: 400,
                  fontFamily: "Roboto",
                  fontSize: "20px",
                  color: "#414958",
                }}
              >
                {t("Attendant")}
              </Typography>
              <FormControl fullWidth>
                <StyledSelect
                  value={formData.attendant}
                  onChange={handleChange("attendant")}
                  displayEmpty
                  disabled={loading}
                >
                  <MenuItem value="" disabled>
                    {t("Select Attendant")}
                  </MenuItem>
                  {attendantData.map((attendant) => (
                    <MenuItem key={attendant.id} value={attendant.id}>
                      {attendant.firstName} {attendant.lastName}
                    </MenuItem>
                  ))}
                </StyledSelect>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} md={3} mt={3}>
          <input
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            style={{ display: "none" }}
            id={`vehicle-image-upload-${isEdit ? "edit" : "create"}`}
            type="file"
            onChange={handleImageUpload}
            disabled={loading}
          />
          <label htmlFor={`vehicle-image-upload-${isEdit ? "edit" : "create"}`}>
            <ImageUploadBox sx={{ position: "relative" }}>
              {imagePreview ? (
                <>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </>
              ) : (
                <Box textAlign="center">
                  <ImageUploadIcon />
                </Box>
              )}
            </ImageUploadBox>
          </label>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 4,
              zIndex: 2,
              justifyContent: "center",
              alignItems: "center",
              marginTop: 1,
            }}
          >
            {/* Edit Icon */}
            <label
              htmlFor={`vehicle-image-upload-${isEdit ? "edit" : "create"}`}
              style={{ cursor: "pointer" }}
            >
              <svg
                width="52"
                height="36"
                viewBox="0 0 52 36"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="0.5"
                  y="0.5"
                  width="51"
                  height="35"
                  rx="7.5"
                  fill="#F2F2F3"
                />
                <rect
                  x="0.5"
                  y="0.5"
                  width="51"
                  height="35"
                  rx="7.5"
                  stroke="#20A8E0"
                />
                <path
                  d="M29.333 9.67676L31.833 12.1768L29.9272 14.0834L27.4272 11.5834L29.333 9.67676ZM19.333 19.6668V22.1668H21.833L28.7488 15.2609L26.2488 12.7609L19.333 19.6668ZM19.333 24.6668H32.6663V26.3334H19.333V24.6668Z"
                  fill="#20A8E0"
                />
              </svg>
            </label>

            {/* Delete Icon */}
            <Box
              component="span"
              onClick={handleProfileImageDelete}
              sx={{ cursor: "pointer" }}
            >
              <svg
                width="52"
                height="36"
                viewBox="0 0 52 36"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="0.5"
                  y="0.5"
                  width="51"
                  height="35"
                  rx="7.5"
                  fill="#F2F2F3"
                />
                <rect
                  x="0.5"
                  y="0.5"
                  width="51"
                  height="35"
                  rx="7.5"
                  stroke="#EE3B2B"
                />
                <path
                  d="M29.3327 11H26.666L25.3327 9.66699H20.666L19.3327 11H16.666V13.667H29.3327V11ZM17.9993 25.667C17.9993 27.1337 19.1993 28.3337 20.666 28.3337H25.9993C27.466 28.3337 28.666 27.1337 28.666 25.667V14.3337H17.9993V25.667ZM20.666 17.0003H25.9993V25.667H20.666V17.0003Z"
                  fill="#EE3B2B"
                />
              </svg>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Box mt={4}>
        <Typography
          variant="h6"
          sx={{ mb: 2, fontWeight: 600, fontSize: "24px", color: "#647490" }}
        >
          {t(
            "Upload Documents (Rc book, Emission Test Report, Insurance Policy)",
          )}
        </Typography>
      </Box>

      <Grid container spacing={2} mt={1}>
        {uploadedDocuments.map((doc, index) => (
          <UploadedBox
            key={index}
            docType={doc.type}
            image={doc.image}
            index={index}
          />
        ))}
        {uploadedDocuments.length < 3 && <UploadBox />}
      </Grid>

      {statusMessage && (
        <Box sx={{ mt: 3 }}>
          <Alert severity={statusMessage.type}>{statusMessage.message}</Alert>
        </Box>
      )}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={3}
      >
        <Box>
          {isEdit && onDelete && (
            <DeleteButton
              onClick={handleDeleteClick}
              startIcon={<DeleteIcon />}
              disabled={loading}
            >
              {t("Delete")}
            </DeleteButton>
          )}
        </Box>
        <Box display="flex" gap="64px">
          {!isEdit && (
            <CancelButton onClick={onClose} disabled={loading}>
              {t("Cancel")}
            </CancelButton>
          )}
          <ConfirmButton onClick={handleSubmit} disabled={loading}>
            {isEdit ? t("Update") : t("Confirm")}
          </ConfirmButton>
        </Box>
        <Box></Box>
      </Box>

      <DeleteModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t("Delete Vehicle!")}
        message={t(
          "Are you sure you want to delete this vehicle? This action cannot be undone.",
        )}
        cancelButtonText={t("Cancel")}
        confirmButtonText={t("Delete")}
      />
    </FormContainer>
  );
}
